import { CommandBar, Dropdown, IColumn, ICommandBarItemProps, mergeStyleSets, MessageBar, MessageBarType, SelectionMode, ShimmeredDetailsList, Stack } from "@fluentui/react"
import { useCallback, useEffect, useState } from "react"
import { safeFormatDateValue } from "../lib/helper"
import { makeACMGCRMApiCall } from "../services/apiService"
import { IOpportunity } from "../types/IOpportunity"
import { StickeyHeaderDetailsList } from "./Shared/StickeyHeaderDetailsList"

const classNames = mergeStyleSets({
	frozenCell: {
		position: "sticky !important",
		left: 0,
		background: "white",
		zIndex: 1,
		borderRight: "1px solid #edebe9",
		selectors: {
			":hover": {
				background: "white"
			}
		}
	},
	frozenHeader: {
		position: "sticky !important",
		left: 0,
		background: "#f3f2f1",
		zIndex: 3,
		borderRight: "1px solid #edebe9"
	}
})

type CompanyScopeQuery = "all" | "mine"

export function OpportunityMonitoring() {
	const [loading, setLoading] = useState<boolean>(true)
	const [error, setError] = useState<string>()
	const [refreshFlag, setRefreshFlag] = useState<boolean>(false)
	const [opportunities, setOpportunities] = useState<IOpportunity[]>([])
	const [filteredOpportunities, setFilteredOpportunities] = useState<IOpportunity[]>([])
	const brokerId = (window.top as any).parent.Microsoft.Dynamic365.Portal.User.contactId
	const [viewFilter, setViewFilter] = useState<CompanyScopeQuery>("mine")

	const fetchOpportunities = useCallback(async () => {
		setLoading(true)
		setError(undefined)
		try {
			const response = await makeACMGCRMApiCall("getOpportunitiesForBroker")
			setOpportunities(response)
		} catch (err) {
			console.error(err)
			setError("Error getting opportunities")
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		fetchOpportunities()
	}, [fetchOpportunities, refreshFlag])

	useEffect(() => {
		let filteredOpportunities = opportunities || []
		if (viewFilter === "mine") {
			filteredOpportunities = filteredOpportunities.filter(opportunity => opportunity.cw_preferredbroker === brokerId)
		}
		setFilteredOpportunities(filteredOpportunities)
	}, [opportunities, viewFilter])

	const getColumns = (): IColumn[] => {
		const columns: IColumn[] = [
			{
				key: "accountname",
				name: "Account Name",
				fieldName: "accountname",
				minWidth: 150,
				isResizable: true,
				className: classNames.frozenCell,
				headerClassName: classNames.frozenHeader,
				onRender: (item: IOpportunity) => {
					return item.accountname
				}
			},
			{
				key: "cw_opportunitystage",
				name: "Opportunity Stage",
				fieldName: "cw_opportunitystage",
				minWidth: 100,
				isResizable: true,
				onRender: (item: IOpportunity) => {
					return item.cw_opportunitystage || "N/A"
				}
			},
			{
				key: "brokername",
				name: "Broker",
				fieldName: "brokername",
				minWidth: 100,
				isResizable: true,
				onRender: (item: IOpportunity) => {
					return item.brokername || ""
				}
			},
			{
				key: "cradf_effectivedate",
				name: "Effective Date",
				fieldName: "cradf_effectivedate",
				minWidth: 100,
				isResizable: true,
				onRender: (item: IOpportunity) => {
					return safeFormatDateValue(item.cradf_effectivedate, "MM/dd/yyyy")
				}
			},
			{
				key: "cw_preppedquotedocs",
				name: "Quote Docs Prepared",
				fieldName: "cw_preppedquotedocs",
				minWidth: 100,
				isResizable: true,
				onRender: (item: IOpportunity) => {
					return safeFormatDateValue(item.cw_preppedquotedocs, "MM/dd/yyyy", "")
				}
			},
			{
				key: "cw_sentplanoptions",
				name: "Quote Sent",
				fieldName: "cw_sentplanoptions",
				minWidth: 100,
				isResizable: true,
				onRender: (item: IOpportunity) => {
					return safeFormatDateValue(item.cw_sentplanoptions, "MM/dd/yyyy", "")
				}
			},
			{
				key: "cw_quotestatus",
				name: "Quote Status",
				fieldName: "cw_quotestatus",
				minWidth: 100,
				isResizable: true,
				onRender: (item: IOpportunity) => {
					return item.cw_quotestatus
				}
			},
			{
				key: "cw_receivedplanoptions",
				name: "Quote Signed",
				fieldName: "cw_receivedplanoptions",
				minWidth: 100,
				isResizable: true,
				onRender: (item: IOpportunity) => {
					return safeFormatDateValue(item.cw_receivedplanoptions, "MM/dd/yyyy", "")
				}
			},
			{
				key: "cw_preppedcontractdocs",
				name: "Contract Docs Prepared",
				fieldName: "cw_preppedcontractdocs",
				minWidth: 100,
				isResizable: true,
				onRender: (item: IOpportunity) => {
					return safeFormatDateValue(item.cw_preppedcontractdocs, "MM/dd/yyyy", "")
				}
			},
			{
				key: "cw_sentdocstogroupforesignature",
				name: "Contract Sent",
				fieldName: "cw_sentdocstogroupforesignature",
				minWidth: 100,
				isResizable: true,
				onRender: (item: IOpportunity) => {
					return safeFormatDateValue(item.cw_sentdocstogroupforesignature, "MM/dd/yyyy", "")
				}
			},
			{
				key: "cw_contractstatus",
				name: "Contract Status",
				fieldName: "cw_contractstatus",
				minWidth: 100,
				isResizable: true,
				onRender: (item: IOpportunity) => {
					return item.cw_contractstatus
				}
			},
			{
				key: "cw_docsesigned",
				name: "Contract Signed",
				fieldName: "cw_docsesigned",
				minWidth: 100,
				isResizable: true,
				onRender: (item: IOpportunity) => {
					return safeFormatDateValue(item.cw_docsesigned, "MM/dd/yyyy", "")
				}
			}
		]

		return columns
	}

	const getCommandBarItems = (): ICommandBarItemProps[] => {
		const items: ICommandBarItemProps[] = [
			{
				key: "filter",
				commandBarButtonAs: () => {
					return (
						<Dropdown
							selectedKey={viewFilter}
							options={[
								{ key: "all", text: "All Opportunities" },
								{ key: "mine", text: "My Opportunities" }
							]}
							style={{ marginRight: 16 }}
							onChange={(_ev, option) => {
								setViewFilter(option.key?.toString() as CompanyScopeQuery)
							}}
						/>
					)
				}
			}
		]
		return items
	}

	return (
		<Stack tokens={{ childrenGap: 10 }} style={{ padding: 15 }}>
			{error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
			{loading && <ShimmeredDetailsList items={[]} columns={getColumns()} enableShimmer />}
			{!loading && filteredOpportunities && (
				<Stack>
					<CommandBar items={getCommandBarItems()} />
					<StickeyHeaderDetailsList items={filteredOpportunities} columns={getColumns()} selectionMode={SelectionMode.none} />
				</Stack>
			)}
		</Stack>
	)
}
